import { generateTimePeriodOptions } from "../../CarbonFootPrinting/utils/PeriodCalculationUtils";

/**
 * True when a question type is one of the quantitative variants (guards against
 * the historical "quatitative" typo persisted in older data).
 * @param {string} type
 * @returns {boolean}
 */
export const isQuantitativeType = (type) => {
  if (!type) return false;
  const t = type.toLowerCase();
  return t === "quantitative" || t === "quatitative" || t === "quantitative_trends" || t === "tabular_question";
};

/**
 * True when date range [startA, endA] overlaps [startB, endB]. Any missing bound → false.
 * @returns {boolean}
 */
export const isOverlap = (startAStr, endAStr, startBStr, endBStr) => {
  if (!startAStr || !endAStr || !startBStr || !endBStr) return false;
  const startA = new Date(startAStr);
  const endA = new Date(endAStr);
  const startB = new Date(startBStr);
  const endB = new Date(endBStr);
  return startA <= endB && endA >= startB;
};

/**
 * Count total data points and completed (ACCEPTED) data points across questions,
 * expanding quantitative questions per location×period and CUSTOM questions per user×period.
 * Optimized with Map/Set indexing and integer timestamp comparisons to avoid main-thread freeze.
 * @returns {{ totalDataPoints: number, completedCount: number }}
 */
export const calculateProgressMetrics = (
  questions = [],
  reportingAnswers = [],
  locationIds = [],
  periods = [],
  filteredUsers = [],
  isQuantitativeType
) => {
  let totalDataPoints = 0;
  let completedCount = 0;

  if (!questions || !questions.length) return { totalDataPoints: 0, completedCount: 0 };

  // 1. Pre-parse period timestamps once
  const parsedPeriods = (periods || []).map(p => ({
    start: p.fromDate ? new Date(p.fromDate).getTime() : 0,
    end: p.toDate ? new Date(p.toDate).getTime() : 0,
  }));

  // 2. Pre-index reportingAnswers into Maps keyed by "qId_locId", "qId_userId", and "qId"
  const answerByQuantMap = new Map();
  const answerByCustomMap = new Map();
  const answerByOtherMap = new Map();

  const answers = reportingAnswers || [];
  for (let i = 0; i < answers.length; i++) {
    const item = answers[i];
    if (item.status !== 'ACCEPTED') continue;

    const qId = Number(item.questionId);
    const start = item.fromDate ? new Date(item.fromDate).getTime() : 0;
    const end = item.toDate ? new Date(item.toDate).getTime() : 0;

    if (item.sourceId != null) {
      const key = `${qId}_${Number(item.sourceId)}`;
      let list = answerByQuantMap.get(key);
      if (!list) {
        list = [];
        answerByQuantMap.set(key, list);
      }
      list.push({ start, end });
    }

    if (item.userId != null) {
      const key = `${qId}_${Number(item.userId)}`;
      let list = answerByCustomMap.get(key);
      if (!list) {
        list = [];
        answerByCustomMap.set(key, list);
      }
      list.push({ start, end });
    }

    answerByOtherMap.set(qId, true);
  }

  // 3. Fast O(1) lookups instead of scanning millions of rows
  const locIds = (locationIds || []).map(Number);
  const users = (filteredUsers || []).map(Number);

  for (let i = 0; i < questions.length; i++) {
    const q = questions[i];
    const qId = Number(q.questionId);
    const isQuant = isQuantitativeType(q.questionType);

    if (isQuant) {
      for (let l = 0; l < locIds.length; l++) {
        const locId = locIds[l];
        const list = answerByQuantMap.get(`${qId}_${locId}`);

        for (let p = 0; p < parsedPeriods.length; p++) {
          totalDataPoints++;
          if (list) {
            const pStart = parsedPeriods[p].start;
            const pEnd = parsedPeriods[p].end;
            const hasCompleted = list.some(item => item.start <= pEnd && item.end >= pStart);
            if (hasCompleted) completedCount++;
          }
        }
      }
    } else {
      if (q.frequency === "CUSTOM") {
        for (let u = 0; u < users.length; u++) {
          const userId = users[u];
          const list = answerByCustomMap.get(`${qId}_${userId}`);

          for (let p = 0; p < parsedPeriods.length; p++) {
            totalDataPoints++;
            if (list) {
              const pStart = parsedPeriods[p].start;
              const pEnd = parsedPeriods[p].end;
              const hasCompleted = list.some(item => item.start <= pEnd && item.end >= pStart);
              if (hasCompleted) completedCount++;
            }
          }
        }
      } else {
        totalDataPoints++;
        if (answerByOtherMap.get(qId)) {
          completedCount++;
        }
      }
    }
  }

  return { totalDataPoints, completedCount };
};

/**
 * Collect the answer records that make up the "merged framework answers" view.
 * Uses Set lookups for O(1) speed.
 */
export const mergeFrameworkAnswers = (
  reportingAnswers = [],
  questionIdsOthers = [],
  customQuestionIds = [],
  fromDates = [],
  effectiveLocationIds = []
) => {
  const locSet = new Set((effectiveLocationIds || []).map(Number));
  const qOthersSet = new Set((questionIdsOthers || []).map(Number));
  const qCustomSet = new Set((customQuestionIds || []).map(Number));
  const dateSet = new Set(fromDates || []);

  const result = [];
  const answers = reportingAnswers || [];
  for (let i = 0; i < answers.length; i++) {
    const item = answers[i];
    if (!locSet.has(Number(item.sourceId))) continue;

    const qId = Number(item.questionId);
    if (qOthersSet.has(qId)) {
      result.push(item);
    } else if (qCustomSet.has(qId) && dateSet.has(item.fromDate)) {
      result.push(item);
    }
  }
  return result;
};

/**
 * Sum the expansion weights of a question list. CUSTOM (and quantitative) questions are
 * counted once per location×period rather than duplicated in the array, so their `weight`
 * carries that multiplier; every other entry defaults to weight 1. Used as the denominator
 * for coverage/progress percentages in place of a bloated `.length`.
 * @param {{ weight?: number }[]} questions
 * @returns {number}
 */
export const sumQuestionWeights = (questions) =>
  questions.reduce((total, q) => total + (q.weight ?? 1), 0);

/** Month names used for period label construction. */
export const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Build period options with human-readable labels anchored to the company's starting month.
 * Examples (startingMonth = 7 / July):
 *   QUARTERLY  → ["Q1 (Jul – Sep)", "Q2 (Oct – Dec)", "Q3 (Jan – Mar)", "Q4 (Apr – Jun)"]
 *   HALF_YEARLY→ ["H1 (Jul – Dec)", "H2 (Jan – Jun)"]
 *   MONTHLY    → ["Jul", "Aug", …, "Jun"]
 *   YEARLY     → ["Jul – Jun"]
 *
 * @param {string} aggregation   - "MONTHLY" | "QUARTERLY" | "HALF_YEARLY" | "YEARLY"
 * @param {number} startingMonth - 1-indexed (e.g. 7 = July)
 * @returns {{ value: string; label: string }[]}
 */
export const buildPeriodOptions = (aggregation, startingMonth) => {
  const base = generateTimePeriodOptions(aggregation, startingMonth);
  if (aggregation === 'QUARTERLY') {
    return base.map((opt, i) => {
      const startIdx = ((startingMonth - 1) + i * 3) % 12;
      const endIdx = (startIdx + 2) % 12;
      return { value: opt.value, label: `Q${i + 1} (${MONTH_NAMES[startIdx]} – ${MONTH_NAMES[endIdx]})` };
    });
  }
  if (aggregation === 'HALF_YEARLY') {
    return base.map((opt, i) => {
      const startIdx = ((startingMonth - 1) + i * 6) % 12;
      const endIdx = (startIdx + 5) % 12;
      return { value: opt.value, label: `H${i + 1} (${MONTH_NAMES[startIdx]} – ${MONTH_NAMES[endIdx]})` };
    });
  }
  return base;
};
